import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Table2, Plus, Download, RefreshCw, Trash2, X, MessageSquare, CheckCircle2,
    Circle, Send, ChevronDown, Upload, FileSpreadsheet, FileText, AlertTriangle
} from 'lucide-react';
import { apiService } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import { SheetGrid, SheetColumn, SheetRow } from './SheetGrid';

interface SheetsPanelProps {
    /** Scope to one project (the project tab). Omitted on the global page. */
    projectId?: string;
}

export const SheetsPanel: React.FC<SheetsPanelProps> = ({ projectId }) => {
    const { user } = useAuth();
    const [sheets, setSheets] = useState<any[]>([]);
    const [activeId, setActiveId] = useState<string | null>(null);
    const [detail, setDetail] = useState<any | null>(null);
    const [projects, setProjects] = useState<any[]>([]);
    const [users, setUsers] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showNew, setShowNew] = useState(false);
    const [commentRow, setCommentRow] = useState<SheetRow | null>(null);
    const [showExportMenu, setShowExportMenu] = useState(false);
    const [showImportMenu, setShowImportMenu] = useState(false);
    const [importMode, setImportMode] = useState<'append' | 'replace'>('append');
    const [importResult, setImportResult] = useState<any | null>(null);
    const fileInputRef = React.useRef<HTMLInputElement | null>(null);

    const isAdmin = useMemo(() => {
        const groups: string[] = (user as any)?.groups || [];
        return groups.some((g) => ['jira-administrators', 'administrators', 'admin'].includes(String(g).toLowerCase()));
    }, [user]);

    const loadSheets = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const list = await apiService.getSheets(projectId ? { project_id: projectId } : undefined);
            const arr = Array.isArray(list) ? list : [];
            setSheets(arr);
            setActiveId((prev) => (prev && arr.some((s: any) => s.id === prev) ? prev : (arr[0]?.id || null)));
        } catch (e: any) {
            setError(e?.message || 'Failed to load sheets');
        } finally {
            setLoading(false);
        }
    }, [projectId]);

    const loadDetail = useCallback(async (id: string) => {
        try {
            setDetail(await apiService.getSheet(id));
        } catch (e: any) {
            setError(e?.message || 'Failed to open sheet');
        }
    }, []);

    useEffect(() => { loadSheets(); }, [loadSheets]);
    useEffect(() => { if (activeId) loadDetail(activeId); else setDetail(null); }, [activeId, loadDetail]);

    useEffect(() => {
        apiService.getUsers().then((u: any) => setUsers(Array.isArray(u) ? u : [])).catch(() => setUsers([]));
        if (!projectId) {
            apiService.getProjects().then((p: any) => setProjects(Array.isArray(p) ? p : [])).catch(() => setProjects([]));
        }
    }, [projectId]);

    const columns: SheetColumn[] = detail?.columns || [];
    const rows: SheetRow[] = detail?.rows || [];

    // ---- grid handlers ----------------------------------------------------

    const handleCellChange = async (rowId: string, columnId: string, value: any) => {
        const updated = await apiService.updateSheetRow(activeId!, rowId, { data: { [columnId]: value } });
        setDetail((prev: any) => prev && ({
            ...prev,
            rows: prev.rows.map((r: SheetRow) => (r.id === rowId ? { ...r, ...updated } : r)),
        }));
    };

    const handlePasteBlock = async (startRow: number, startCol: number, matrix: string[][]) => {
        const payloadRows = matrix.map((line) => {
            const obj: Record<string, any> = {};
            line.forEach((cell, i) => {
                const col = columns[startCol + i];
                if (col) obj[col.id] = cell;
            });
            return obj;
        });
        setBusy(true);
        try {
            await apiService.bulkWriteSheetRows(activeId!, { start_index: startRow, rows: payloadRows });
            await loadDetail(activeId!);
            await loadSheets();
        } finally {
            setBusy(false);
        }
    };

    const handleAddRow = async () => {
        setBusy(true);
        try {
            await apiService.addSheetRow(activeId!, {});
            await loadDetail(activeId!);
        } catch (e: any) {
            setError(e?.message || 'Could not add a row');
        } finally {
            setBusy(false);
        }
    };

    const handleDeleteRow = async (rowId: string) => {
        if (!window.confirm('Delete this row and its comments?')) return;
        try {
            await apiService.deleteSheetRow(activeId!, rowId);
            if (commentRow?.id === rowId) setCommentRow(null);
            await loadDetail(activeId!);
        } catch (e: any) {
            setError(e?.message || 'Could not delete the row');
        }
    };

    const handleAddColumn = async () => {
        try {
            await apiService.addSheetColumn(activeId!, { name: 'New field', type: 'text' });
            await loadDetail(activeId!);
        } catch (e: any) {
            setError(e?.message || 'Could not add a field');
        }
    };

    const handleColumnUpdate = async (columnId: string, patch: Record<string, any>) => {
        try {
            await apiService.updateSheetColumn(activeId!, columnId, patch);
            await loadDetail(activeId!);
        } catch (e: any) {
            setError(e?.message || 'Could not update the field');
        }
    };

    const handleColumnDelete = async (columnId: string) => {
        if (!window.confirm('Delete this field and all of its values?')) return;
        try {
            await apiService.deleteSheetColumn(activeId!, columnId);
            await loadDetail(activeId!);
        } catch (e: any) {
            setError(e?.message || 'Could not delete the field');
        }
    };

    const handleExport = async () => {
        try {
            const { blob, filename } = await apiService.exportSheet(activeId!);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename || `${detail?.name || 'sheet'}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e: any) {
            setError(e?.message || 'Export failed');
        }
    };

    const handleExportXlsx = async () => {
        setShowExportMenu(false);
        try {
            const { blob, filename } = await apiService.exportSheetXlsx(activeId!);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename || `${detail?.name || 'sheet'}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e: any) {
            setError(e?.message || 'Excel export failed');
        }
    };

    const pickFile = (mode: 'append' | 'replace') => {
        setImportMode(mode);
        setShowImportMenu(false);
        setImportResult(null);
        // reset so choosing the same file twice still fires onChange
        if (fileInputRef.current) fileInputRef.current.value = '';
        fileInputRef.current?.click();
    };

    const handleFileChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !activeId) return;
        if (importMode === 'replace' &&
            !window.confirm(`Replace every row in "${detail?.name}" with the contents of ${file.name}?`)) {
            return;
        }
        setBusy(true);
        setError(null);
        try {
            const result = await apiService.importSheetFile(activeId, file, importMode);
            setImportResult(result);
            await loadDetail(activeId);
            await loadSheets();
        } catch (err: any) {
            setError(err?.message || 'Import failed');
        } finally {
            setBusy(false);
        }
    };

    const handleDeleteSheet = async () => {
        if (!detail) return;
        if (!window.confirm(`Delete the sheet "${detail.name}" with all of its rows and comments?`)) return;
        try {
            await apiService.deleteSheet(detail.id);
            setActiveId(null);
            setDetail(null);
            await loadSheets();
        } catch (e: any) {
            setError(e?.message || 'Could not delete the sheet');
        }
    };

    // ---- render -----------------------------------------------------------

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <Table2 className="w-5 h-5 text-emerald-600" />
                    <h2 className="text-base font-semibold text-gray-900">Sheets</h2>
                    <span className="text-xs text-gray-500">
                        {loading ? 'loading…' : `${sheets.length} sheet${sheets.length === 1 ? '' : 's'}`}
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    {detail && (
                        <>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".xlsx,.xlsm,.csv"
                                className="hidden"
                                onChange={handleFileChosen}
                            />

                            <div className="relative">
                                <button onClick={() => { setShowImportMenu(!showImportMenu); setShowExportMenu(false); }}
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50">
                                    <Upload className="w-3.5 h-3.5" /> Import
                                    <ChevronDown className="w-3 h-3 text-gray-400" />
                                </button>
                                {showImportMenu && (
                                    <div className="absolute right-0 mt-1 w-56 bg-white border border-gray-200 rounded-xl shadow-lg z-30 py-1">
                                        <button onClick={() => pickFile('append')}
                                            className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50">
                                            <div className="font-medium text-gray-800">Add rows from a file</div>
                                            <div className="text-[11px] text-gray-500">Keeps what is already here</div>
                                        </button>
                                        <button onClick={() => pickFile('replace')}
                                            className="w-full text-left px-3 py-2 text-xs hover:bg-red-50">
                                            <div className="font-medium text-red-700">Replace all rows</div>
                                            <div className="text-[11px] text-gray-500">Deletes existing rows first</div>
                                        </button>
                                        <div className="px-3 py-1.5 text-[11px] text-gray-400 border-t border-gray-100">
                                            .xlsx, .xlsm or .csv · first row is the heading
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="relative">
                                <button onClick={() => { setShowExportMenu(!showExportMenu); setShowImportMenu(false); }}
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50">
                                    <Download className="w-3.5 h-3.5" /> Export
                                    <ChevronDown className="w-3 h-3 text-gray-400" />
                                </button>
                                {showExportMenu && (
                                    <div className="absolute right-0 mt-1 w-52 bg-white border border-gray-200 rounded-xl shadow-lg z-30 py-1">
                                        <button onClick={handleExportXlsx}
                                            className="w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-gray-50">
                                            <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-600" />
                                            <span className="font-medium text-gray-800">Excel (.xlsx)</span>
                                        </button>
                                        <button onClick={() => { setShowExportMenu(false); handleExport(); }}
                                            className="w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-gray-50">
                                            <FileText className="w-3.5 h-3.5 text-gray-500" />
                                            <span className="font-medium text-gray-800">CSV</span>
                                        </button>
                                    </div>
                                )}
                            </div>

                            <button onClick={() => loadDetail(detail.id)}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50">
                                <RefreshCw className={`w-3.5 h-3.5 ${busy ? 'animate-spin' : ''}`} /> Refresh
                            </button>
                        </>
                    )}
                    <button onClick={() => setShowNew(true)}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700">
                        <Plus className="w-3.5 h-3.5" /> New sheet
                    </button>
                </div>
            </div>

            {error && (
                <div className="flex items-start gap-2 px-3 py-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg">
                    <span className="flex-1">{error}</span>
                    <button onClick={() => setError(null)}><X className="w-3.5 h-3.5" /></button>
                </div>
            )}

            {importResult && (
                <div className="flex items-start gap-2 px-3 py-2 text-xs bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-800">
                    <div className="flex-1 space-y-1">
                        <div className="font-medium">
                            {importResult.message} of {importResult.rows_in_file} in the file
                            {importResult.mode === 'replace' ? ' (replaced existing rows)' : ''}
                        </div>
                        {importResult.columns_created?.length > 0 && (
                            <div>New fields created: {importResult.columns_created.join(', ')}</div>
                        )}
                        {importResult.error_count > 0 && (
                            <div className="text-amber-800">
                                <div className="flex items-center gap-1 font-medium">
                                    <AlertTriangle className="w-3 h-3" />
                                    {importResult.error_count} cell{importResult.error_count === 1 ? '' : 's'} skipped:
                                </div>
                                <ul className="list-disc ml-4 mt-0.5">
                                    {importResult.errors.slice(0, 5).map((msg: string, i: number) => <li key={i}>{msg}</li>)}
                                </ul>
                            </div>
                        )}
                    </div>
                    <button onClick={() => setImportResult(null)}><X className="w-3.5 h-3.5" /></button>
                </div>
            )}

            {/* sheet tabs */}
            {sheets.length > 0 && (
                <div className="flex items-center gap-1 flex-wrap border-b border-gray-200 pb-2">
                    {sheets.map((s) => (
                        <button key={s.id} onClick={() => setActiveId(s.id)}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg transition-colors ${activeId === s.id
                                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                : 'text-gray-600 border border-transparent hover:bg-gray-50'}`}>
                            {!projectId && s.project?.key && (
                                <span className="text-[10px] px-1 bg-gray-100 text-gray-600 rounded">{s.project.key}</span>
                            )}
                            {s.name}
                            <span className="text-[10px] text-gray-400">{s.row_count}</span>
                            {s.open_comment_count > 0 && (
                                <span className="inline-flex items-center gap-0.5 text-[10px] text-amber-600">
                                    <MessageSquare className="w-2.5 h-2.5" />{s.open_comment_count}
                                </span>
                            )}
                        </button>
                    ))}
                </div>
            )}

            {!loading && sheets.length === 0 && (
                <div className="p-10 text-center bg-white border border-gray-200 rounded-xl">
                    <Table2 className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm font-medium text-gray-700">No sheets yet</p>
                    <p className="mt-1 text-xs text-gray-500 max-w-md mx-auto">
                        A sheet is a table your team defines: add your own fields, paste data straight from Excel,
                        and leave comments on any row.
                    </p>
                    <button onClick={() => setShowNew(true)}
                        className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700">
                        <Plus className="w-3.5 h-3.5" /> Create the first sheet
                    </button>
                </div>
            )}

            {detail && (
                <div className="flex gap-3">
                    <div className="flex-1 min-w-0 space-y-2">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-sm font-semibold text-gray-900">{detail.name}</h3>
                                {detail.description && <p className="text-xs text-gray-500">{detail.description}</p>}
                            </div>
                            {(isAdmin || true) && (
                                <button onClick={handleDeleteSheet} title="Delete sheet"
                                    className="text-gray-300 hover:text-red-600">
                                    <Trash2 className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </div>
                        <SheetGrid
                            columns={columns}
                            rows={rows}
                            users={users}
                            busy={busy}
                            onCellChange={handleCellChange}
                            onPasteBlock={handlePasteBlock}
                            onAddRow={handleAddRow}
                            onDeleteRow={handleDeleteRow}
                            onAddColumn={handleAddColumn}
                            onColumnUpdate={handleColumnUpdate}
                            onColumnDelete={handleColumnDelete}
                            onOpenComments={(row) => setCommentRow(row)}
                        />
                    </div>

                    {commentRow && (
                        <CommentsDrawer
                            sheetId={detail.id}
                            row={commentRow}
                            columns={columns}
                            onClose={() => setCommentRow(null)}
                            onChanged={async () => { await loadDetail(detail.id); await loadSheets(); }}
                        />
                    )}
                </div>
            )}

            {showNew && (
                <NewSheetModal
                    projectId={projectId}
                    projects={projects}
                    onClose={() => setShowNew(false)}
                    onCreated={async (created) => {
                        setShowNew(false);
                        await loadSheets();
                        setActiveId(created.id);
                    }}
                />
            )}
        </div>
    );
};

// --------------------------------------------------------------------------- //

interface CommentsDrawerProps {
    sheetId: string;
    row: SheetRow;
    columns: SheetColumn[];
    onClose: () => void;
    onChanged: () => Promise<void>;
}

const CommentsDrawer: React.FC<CommentsDrawerProps> = ({ sheetId, row, columns, onClose, onChanged }) => {
    const [comments, setComments] = useState<any[]>([]);
    const [body, setBody] = useState('');
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    const primary = columns.find((c) => c.is_primary) || columns[0];
    const label = primary ? (row.data?.[primary.id] || `Row ${row.position}`) : `Row ${row.position}`;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            setComments(await apiService.getSheetRowComments(sheetId, row.id));
        } catch (e: any) {
            setErr(e?.message || 'Failed to load comments');
        } finally {
            setLoading(false);
        }
    }, [sheetId, row.id]);

    useEffect(() => { load(); }, [load]);

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!body.trim()) return;
        setSending(true);
        setErr(null);
        try {
            await apiService.addSheetRowComment(sheetId, row.id, body.trim());
            setBody('');
            await load();
            await onChanged();
        } catch (e: any) {
            setErr(e?.message || 'Could not post the comment');
        } finally {
            setSending(false);
        }
    };

    const toggleResolved = async (comment: any) => {
        try {
            await apiService.updateSheetComment(sheetId, comment.id, { resolved: !comment.resolved });
            await load();
            await onChanged();
        } catch (e: any) {
            setErr(e?.message || 'Could not update the comment');
        }
    };

    const remove = async (comment: any) => {
        if (!window.confirm('Delete this comment?')) return;
        try {
            await apiService.deleteSheetComment(sheetId, comment.id);
            await load();
            await onChanged();
        } catch (e: any) {
            setErr(e?.message || 'Could not delete the comment');
        }
    };

    return (
        <div className="w-80 shrink-0 bg-white border border-gray-200 rounded-xl flex flex-col max-h-[calc(100vh-320px)]">
            <div className="flex items-start justify-between gap-2 px-3 py-2 border-b border-gray-100">
                <div className="min-w-0">
                    <div className="text-xs font-semibold text-gray-900">Row comments</div>
                    <div className="text-[11px] text-gray-500 truncate" title={String(label)}>{String(label)}</div>
                </div>
                <button onClick={onClose} className="text-gray-400 hover:text-gray-600 shrink-0">
                    <X className="w-4 h-4" />
                </button>
            </div>

            <div className="flex-1 overflow-y-auto px-3 py-2 space-y-2">
                {loading ? (
                    <p className="text-xs text-gray-500">Loading…</p>
                ) : comments.length === 0 ? (
                    <p className="text-xs text-gray-500">
                        No comments on this row yet. Use this to leave feedback for the team about this page.
                    </p>
                ) : comments.map((c) => (
                    <div key={c.id} className={`p-2 rounded-lg border text-xs ${c.resolved ? 'bg-gray-50 border-gray-100 opacity-70' : 'bg-white border-gray-200'}`}>
                        <div className="flex items-center justify-between gap-2 mb-1">
                            <span className="font-medium text-gray-800 truncate">
                                {c.author?.display_name || c.author?.email_address || 'Unknown'}
                            </span>
                            <span className="text-[10px] text-gray-400 shrink-0">
                                {c.created_at ? new Date(c.created_at).toLocaleString() : ''}
                            </span>
                        </div>
                        <p className={`text-gray-700 whitespace-pre-wrap ${c.resolved ? 'line-through' : ''}`}>{c.body}</p>
                        <div className="flex items-center gap-2 mt-1.5">
                            <button onClick={() => toggleResolved(c)}
                                className="inline-flex items-center gap-1 text-[11px] text-gray-500 hover:text-emerald-600">
                                {c.resolved ? <CheckCircle2 className="w-3 h-3 text-emerald-600" /> : <Circle className="w-3 h-3" />}
                                {c.resolved ? 'Resolved' : 'Mark resolved'}
                            </button>
                            <button onClick={() => remove(c)} className="text-[11px] text-gray-400 hover:text-red-600">Delete</button>
                        </div>
                    </div>
                ))}
            </div>

            {err && <div className="mx-3 mb-2 px-2 py-1 text-[11px] text-red-700 bg-red-50 border border-red-200 rounded">{err}</div>}

            <form onSubmit={submit} className="px-3 py-2 border-t border-gray-100">
                <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={2}
                    placeholder="Leave feedback on this page…"
                    className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-lg resize-none" />
                <button type="submit" disabled={sending || !body.trim()}
                    className="mt-1.5 w-full inline-flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                    <Send className="w-3 h-3" /> {sending ? 'Posting…' : 'Comment'}
                </button>
            </form>
        </div>
    );
};

// --------------------------------------------------------------------------- //

interface NewSheetModalProps {
    projectId?: string;
    projects: any[];
    onClose: () => void;
    onCreated: (sheet: any) => Promise<void>;
}

const NewSheetModal: React.FC<NewSheetModalProps> = ({ projectId, projects, onClose, onCreated }) => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [project, setProject] = useState(projectId || '');
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        setErr(null);
        if (!project) { setErr('Please choose a project'); return; }
        if (!name.trim()) { setErr('Please give the sheet a name'); return; }
        setSaving(true);
        try {
            const created = await apiService.createSheet({
                project_id: project, name: name.trim(), description: description.trim(), initial_rows: 5,
            });
            await onCreated(created);
        } catch (e: any) {
            setErr(e?.message || 'Could not create the sheet');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-md bg-white rounded-2xl shadow-xl">
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
                    <h3 className="text-sm font-semibold text-gray-900">New sheet</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
                </div>
                <form onSubmit={submit} className="px-5 py-4 space-y-3">
                    <p className="text-xs text-gray-500">
                        Starts with Page, Status, Owner and Notes fields — rename, retype or delete any of them,
                        and add your own.
                    </p>
                    {!projectId && (
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Project *</label>
                            <select value={project} onChange={(e) => setProject(e.target.value)}
                                className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg">
                                <option value="">Select a project…</option>
                                {projects.map((p: any) => <option key={p.id} value={p.id}>{p.key} — {p.name}</option>)}
                            </select>
                        </div>
                    )}
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Name *</label>
                        <input value={name} onChange={(e) => setName(e.target.value)}
                            placeholder="Page level tracker"
                            className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg" />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                        <input value={description} onChange={(e) => setDescription(e.target.value)}
                            className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg" />
                    </div>
                    {err && <div className="px-2.5 py-1.5 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg">{err}</div>}
                    <div className="flex items-center justify-end gap-2 pt-1">
                        <button type="button" onClick={onClose}
                            className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
                        <button type="submit" disabled={saving}
                            className="px-3 py-1.5 text-xs font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50">
                            {saving ? 'Creating…' : 'Create sheet'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};
